import React, { useEffect, useMemo, useState } from "react";
import Inputz from "../atoms/Inputz";
import Buttonz from "../atoms/Buttonz";
import { submitEmailChangeFromSettings } from "../../utils/settingEmailValidation";
import { useDispatch } from "react-redux";
import CenterTitleZ from "../atoms/CenterTitleZ";
import { refreshCurrentUser } from "../../auth/authApi";
import { validationPopup } from "../../redux/actions";

const EMAIL_CHANGE_PENDING_KEY = "uud:emailChangePending";
const EMAIL_CHANGE_PW_KEY = "uud:emailChangePw";

function safeParseJson(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function SettingEmail({ userData, title, icon, content, onEmailUpdated }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(null); // { email, expiresAtMs }
  const [countdown, setCountdown] = useState(null);
  const dispatch = useDispatch();

  const isPendingActive = useMemo(() => {
    if (!pending?.email || !pending?.expiresAtMs) return false;
    return pending.expiresAtMs > Date.now();
  }, [pending]);

  useEffect(() => {
    const raw = safeParseJson(localStorage.getItem(EMAIL_CHANGE_PENDING_KEY));
    if (raw?.email && raw?.expiresAtMs && Number(raw.expiresAtMs) > Date.now()) {
      setPending({ email: String(raw.email), expiresAtMs: Number(raw.expiresAtMs) });
    } else if (raw) {
      localStorage.removeItem(EMAIL_CHANGE_PENDING_KEY);
    }
  }, []);

  useEffect(() => {
    if (!isPendingActive) {
      setCountdown(null);
      return undefined;
    }
    const id = setInterval(() => {
      const diff = pending.expiresAtMs - Date.now();
      if (diff <= 0) {
        setCountdown("Expired");
        localStorage.removeItem(EMAIL_CHANGE_PENDING_KEY);
        setPending(null);
        clearInterval(id);
      } else {
        const minutes = Math.floor(diff / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown(`${minutes}m ${seconds}s`);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isPendingActive, pending?.expiresAtMs]);

  // Poll PocketBase for confirmation (user clicked link in email).
  useEffect(() => {
    if (!isPendingActive) return undefined;

    const intervalMs = 8000;
    let cancelled = false;

    const tick = async () => {
      try {
        const me = await refreshCurrentUser();
        if (cancelled) return;
        const updatedEmail = String(me?.email || "").trim().toLowerCase();
        const target = String(pending?.email || "").trim().toLowerCase();
        if (updatedEmail && target && updatedEmail === target) {
          localStorage.removeItem(EMAIL_CHANGE_PENDING_KEY);
          setPending(null);
          setCountdown(null);
          dispatch(
            validationPopup({
              message:
                "Email confirmed. Your email has been updated.",
              icon: "like",
              type: "success",
            })
          );
          onEmailUpdated?.(me);
        }
      } catch {
        // ignore: keep polling until expiry
      }
    };

    const id = setInterval(tick, intervalMs);
    tick();

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [dispatch, isPendingActive, onEmailUpdated, pending?.email]);

  const handleChange = (e) => {
    const { name, value } = e;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    const result = await submitEmailChangeFromSettings(
      dispatch,
      formData,
      userData?.email,
      "en"
    );
    if (!result.success) {
      setErrors(result.errors || {});
    } else {
      setFormData({ email: "", password: "" });
      setErrors({});
      if (result.pendingEmail && result.expiresAtMs) {
        const next = {
          email: String(result.pendingEmail),
          expiresAtMs: Number(result.expiresAtMs),
        };
        setPending(next);
        localStorage.setItem(EMAIL_CHANGE_PENDING_KEY, JSON.stringify(next));
        // Keep password only for up to 15 minutes (localStorage) so clicking
        // the email link in another tab can still auto-complete confirmation.
        if (formData?.password) {
          localStorage.setItem(
            EMAIL_CHANGE_PW_KEY,
            JSON.stringify({ password: String(formData.password), expiresAtMs: next.expiresAtMs })
          );
        }
      }
    }
  };

  return (
    <>
      <div className="form-def-box">
        <CenterTitleZ
          title={title}
          icon={icon}
          content={content}
          className="setting-profile-title"
        />

        {isPendingActive ? (
          <div className="text-hint-box" style={{ marginTop: "0.75rem" }}>
            We sent a confirmation link to <b>{pending.email}</b>.
            <br />
            Please open it to finish changing your email.
            <br />
            {countdown ? (
              <>
                Time remaining: <b>{countdown}</b>
              </>
            ) : (
              <>Link expires in 15 minutes.</>
            )}
          </div>
        ) : (
          <>
            <p className="setting-email-hint" style={{ marginBottom: "1rem" }}>
              Enter your new email and current password. We will send a
              confirmation link to your new email; open it to apply the change.
            </p>

            <Inputz
              iconL="new_mail"
              // focusL="mail"
              hovericonR="mail"
              label="New email address"
              type="email"
              placeholder="you@example.com"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={errors?.email}
            />
            <Inputz
              iconL="password"
              iconR="ok"
              focusR="cal"
              iconOn="eye_on"
              iconOff="eye_off"
              label="Current password"
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              error={errors?.password}
            />
          </>
        )}
      </div>
      <div className="form-def-box">
        {isPendingActive ? (
          <Buttonz
            label="Cancel request"
            className="secondary"
            onClick={() => {
              localStorage.removeItem(EMAIL_CHANGE_PENDING_KEY);
              localStorage.removeItem(EMAIL_CHANGE_PW_KEY);
              setPending(null);
              setCountdown(null);
            }}
          />
        ) : (
          <Buttonz
            iconR="ok"
            hoverR="ok"
            label="Change email"
            onClick={handleSubmit}
          />
        )}
      </div>
    </>
  );
}

export default SettingEmail;
